'use client';

import { useEffect } from 'react';
import getSearchResultsFact from '@/analytics/helpers/cventAnalytics/getSearchResultsFact';
import type { AdViewFact } from '@/analytics/models/cventAnalytics/AdViewFact';
import getAdViewFact from '@/analytics/helpers/cventAnalytics/getAdViewFact';
import useAnalytics from '@/hooks/useAnalytics';
import type { SearchResultsFact } from '@/analytics/models/cventAnalytics/SearchResultsFact';
import useMap from '@/hooks/useMap';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import { CSN_FACT_SOURCE_VENUE_CARD } from '@/utils/analyticsClientHelper';
import getSearchResultsPageFact from '@/analytics/helpers/siteCatalyst/getSearchResultsPageFact';
import useSiteCatalyst from '@/hooks/useSiteCatalyst';
import type { SearchParams } from '@/models/SearchParams';
import getFilterSortFact from '@/analytics/helpers/cventAnalytics/getFilterSortFact';
import type { FilterSortFact } from '@/analytics/models/cventAnalytics/FilterSortFact';
import type { Filter } from '@/models/Filter';
import type { HubPages } from '@/fetchers/getHubPages';
import reportSiteCatalystFact from '@/analytics/siteCatalystAnalyticsReporter';
import reportToCventAdTrack from '@/analytics/cventAdTrackReporter';
import useLogger from '@/hooks/useLogger';
import useSpinner from '@/hooks/useSpinner';

type Props = {
  searchTerm: string;
  rfpId: string | undefined;
  threeDiamondVenues: CsnSearchVenue[];
  page: number;
  total: number;
  venues: CsnSearchVenue[];
  competitiveMarketVenues: CsnSearchVenue[];
  videoVenues: CsnSearchVenue[];
  isLoggedIn: boolean;
  accountId: string;
  accountIdHash: string;
  filters: Filter;
  sortConfigVariantId: number;
  referrerHeader: string;
  searchParams: SearchParams;
  isFastResponderEnabled: number;
  adSessionId: string;
  csnSearchAppServiceBaseUrl: string;
  hubPages?: HubPages;
  environment: string;
};
export default function LogSearchResultsAndImpressions({
  searchTerm,
  rfpId,
  threeDiamondVenues,
  page,
  total,
  venues,
  competitiveMarketVenues,
  videoVenues,
  isLoggedIn,
  accountId,
  accountIdHash,
  filters,
  sortConfigVariantId,
  referrerHeader,
  searchParams,
  isFastResponderEnabled,
  adSessionId,
  csnSearchAppServiceBaseUrl,
  environment,
  hubPages
}: Props): JSX.Element {
  const { sort, ai: isAIMode } = searchParams;
  const analytics = useAnalytics();
  const { mapView } = useMap();
  const siteCatalyst = useSiteCatalyst();
  const logger = useLogger('LogSearchResultsAndImpressions');
  const { showSpinner } = useSpinner();

  useEffect(() => {
    if (!showSpinner) {
      // 3 diamond ads fact
      const adViewFact: AdViewFact = getAdViewFact(
        searchTerm,
        rfpId,
        threeDiamondVenues.filter(venue => venue.featuredLevel === 'THREE_DIAMOND'),
        mapView,
        CSN_FACT_SOURCE_VENUE_CARD,
        'three_diamond_list'
      );

      // results + other ads fact
      const searchResultsFact = getSearchResultsFact(
        mapView,
        searchTerm,
        page,
        total,
        venues,
        competitiveMarketVenues,
        videoVenues,
        isFastResponderEnabled,
        isAIMode,
        hubPages
      );

      // Filters fact
      const filterFact = getFilterSortFact(
        accountIdHash,
        JSON.stringify(filters),
        sortConfigVariantId,
        page,
        searchTerm,
        mapView,
        sort ?? 'CVENT_PICKS',
        referrerHeader,
        isAIMode
      );

      analytics.reportFact<FilterSortFact>(filterFact);
      analytics.reportFact<SearchResultsFact>(searchResultsFact);
      analytics.reportFact<AdViewFact>(adViewFact);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, searchParams, showSpinner]);

  // log results to site catalyst too
  useEffect(() => {
    function logSiteCatalystFact() {
      const searchResultsPagefact: object = getSearchResultsPageFact(
        venues,
        threeDiamondVenues,
        competitiveMarketVenues,
        videoVenues,
        isLoggedIn,
        searchTerm,
        // TODO: Replace hardcoded initial value with search type
        'initial',
        accountId
      );
      reportSiteCatalystFact(searchResultsPagefact);
    }
    if (siteCatalyst.isSiteCatLoaded) {
      logSiteCatalystFact();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteCatalyst.isSiteCatLoaded]);

  // report diamond plus ad viewed on page load
  useEffect(() => {
    const diamondPlusAd = venues.find(
      venue => venue.featuredLevel === 'DIAMOND_PLUS' || venue.featuredLevel === 'DIAMOND_PLUS_BRAND'
    );
    if (diamondPlusAd) {
      const adData = [
        {
          supplierId: diamondPlusAd.id,
          adCode: diamondPlusAd.listingCode,
          competitiveSetFlag: false,
          needDatesFlag: false
        }
      ];
      reportToCventAdTrack(csnSearchAppServiceBaseUrl, adData, adSessionId, rfpId, 'AD_VIEWED', logger, environment);
    }
  }, [adSessionId, rfpId, csnSearchAppServiceBaseUrl, venues, logger, environment]);

  return <></>;
}
